export const iconColorPresets = {
  tick_circle: {
    filled: {
      fill: "#E6765A",
    },
  },
  warning: {
    error: {
      background: "#F2B8B5",
      triangle: "#951830",
      mark: "#222233",
    },
    warning: {
      background: "#FFF1B8",
      triangle: "#8A6500",
      mark: "#222233",
    },
  },
} as const;

const defineIconLayers = <
  const Defaults extends Record<string, string>,
>(definition: {
  defaults: Defaults;
  order: readonly (keyof Defaults & string)[];
}) => definition;

export const iconLayerDefinitions = {
  apple_music: defineIconLayers({
    defaults: { background: "#FA5A71", mark: "white" },
    order: ["background", "mark"],
  }),
  access: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  add_queue: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  album_view: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  apple: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  atom: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  brush: defineIconLayers({
    defaults: { background: "white", detail: "black" },
    order: ["background", "detail"],
  }),
  calendar: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  cash: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  chat: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  check: defineIconLayers({
    defaults: { fill: "#50CD89", mark: "#222233" },
    order: ["fill", "mark"],
  }),
  checkmark_curved: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  checkbox: defineIconLayers({
    defaults: {
      fill: "transparent",
      stroke: "white",
      check: "transparent",
    },
    order: ["fill", "stroke", "check"],
  }),
  chevron: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  clock: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  crown: defineIconLayers({
    defaults: { background: "black", crown: "#F6B452" },
    order: ["background", "crown"],
  }),
  circle_cross: defineIconLayers({
    defaults: { background: "#092C37", cross: "white" },
    order: ["background", "cross"],
  }),
  close_eye: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  contact: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  cross: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  down_arrow: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  drag_handle: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  disabled: defineIconLayers({
    defaults: { color: "white" },
    order: ["color", "color"],
  }),
  dj_set: defineIconLayers({
    defaults: {
      shade1: "#B9BEC6",
      shade2: "#E9EAEE",
      shade3: "#F5F5F5",
      shade4: "#A7ACB7",
      shade5: "#BBC0C8",
      shade6: "#ACB2BC",
      shade7: "#C0C4CC",
    },
    order: [
      "shade1",
      "shade2",
      "shade3",
      "shade4",
      "shade5",
      "shade6",
      "shade7",
    ],
  }),
  download: defineIconLayers({
    // The arrow is full white like the neighbouring Share icon — the old
    // 50%-white symbol made the enabled button read as disabled (LIS-602).
    defaults: {
      background: "#171A1C",
      border: "rgba(255,255,255,0.4)",
      symbol: "#FFFFFF",
    },
    order: ["background", "border", "background", "border", "symbol"],
  }),
  download_premium: defineIconLayers({
    defaults: {
      orange1: "#EA8257",
      orange2: "#F09253",
      orange3: "#E26A5D",
      orange4: "#DD595F",
      symbol: "#FFEEF8",
    },
    order: [
      "orange1",
      "orange2",
      "orange1",
      "orange3",
      "orange4",
      "orange3",
      "symbol",
    ],
  }),
  downloaded: defineIconLayers({
    defaults: { background: "#FFB75E", symbol: "black" },
    order: ["background", "symbol"],
  }),
  edit: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  empty_playlist: defineIconLayers({
    defaults: {
      back: "#1E2325",
      middle: "#1D1E22",
      front: "#161B1C",
      detail: "#41464E",
    },
    order: ["back", "middle", "front", "detail"],
  }),
  ellipsis: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  extlink: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  flame: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  freemiumLogo: defineIconLayers({
    defaults: {
      background: "#1C142E",
      mark: "#828296",
      highlight: "#CCCCCC",
    },
    order: ["background", "mark", "highlight"],
  }),
  gem: defineIconLayers({
    defaults: {
      top: "#F3D3FB",
      highlight: "rgba(255,255,255,0.5)",
      facet1: "#8D2EE7",
      facet2: "#9937EB",
      facet3: "#C960FB",
      facet4: "#7D1DCF",
      facet5: "#3F0698",
      facet6: "#AB7EE5",
      facet7: "#AC5EF9",
      facet8: "#DB9EFD",
      edge: "white",
    },
    order: [
      "top",
      "highlight",
      "facet1",
      "facet2",
      "facet3",
      "facet4",
      "facet5",
      "facet6",
      "facet7",
      "highlight",
      "facet8",
      "highlight",
      "edge",
    ],
  }),
  globe: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  google: defineIconLayers({
    defaults: {
      red: "#EA4335",
      blue: "#4285F4",
      yellow: "#FBBC05",
      green: "#34A853",
    },
    order: ["red", "blue", "yellow", "green"],
  }),
  hearts: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  help: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  inbox_unread: defineIconLayers({
    defaults: {
      stroke: "white",
      unreadDot: "#F6B452",
    },
    order: ["stroke", "unreadDot", "stroke"],
  }),
  inbox: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  info: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  infinity: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  import_complete: defineIconLayers({
    defaults: {
      back: "#669D77",
      middle: "#2A614A",
      front: "#A1EDB0",
      mark: "white",
    },
    order: ["back", "middle", "front", "mark"],
  }),
  info_filled: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  leaderboard: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  library: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  like_active: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  like_dormant: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  link: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  lock_locked: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  lock_unlocked: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  location_pin: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  loop: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  loop_current: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  mail: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  merch: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  minus_simple: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  modal_cross: defineIconLayers({
    defaults: { background: "#2F2E3E", cross: "white" },
    order: ["background", "cross"],
  }),
  offline: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  offline_mode: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  open_eye: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  pause: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  pencil: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  play: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  playlists: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  plus_simple: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  plus_circle: defineIconLayers({
    defaults: { background: "#E6765A", plus: "#222233" },
    order: ["background", "plus"],
  }),
  remove_from_playlist: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  re_order: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  rename: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  revenue: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  round_checkbox: defineIconLayers({
    defaults: {
      fill: "transparent",
      stroke: "white",
      check: "transparent",
    },
    order: ["fill", "stroke", "check"],
  }),
  round_plus: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  search: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  scanner: defineIconLayers({
    defaults: { color: "black" },
    order: ["color"],
  }),
  select: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  selected: defineIconLayers({
    defaults: {
      fill: "white",
      check: "#222233",
    },
    order: ["fill", "check"],
  }),
  share_upload: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  share: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  shuffle: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  settings: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  skip: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  soundcloud: defineIconLayers({
    defaults: { background: "#FF4300", mark: "white" },
    order: ["background", "mark"],
  }),
  spotify: defineIconLayers({
    defaults: { background: "black", mark: "#65D46E" },
    order: ["background", "mark"],
  }),
  star: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  star_grey_circle: defineIconLayers({
    defaults: {
      background: "rgba(255,255,255,0.1)",
      star: "#909090",
    },
    order: ["background", "star"],
  }),
  tick_circle: defineIconLayers({
    defaults: {
      fill: "transparent",
      stroke: "white",
    },
    order: ["fill", "stroke"],
  }),
  text_box: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  tabs_experiences: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  tabs_home: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  ticket: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  timer: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  trash: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  video: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  view_artist: defineIconLayers({
    defaults: { color: "white" },
    order: ["color"],
  }),
  warning: defineIconLayers({
    defaults: iconColorPresets.warning.error,
    order: ["background", "triangle", "background", "mark"],
  }),
} as const;

export type LayeredIconName = keyof typeof iconLayerDefinitions;

export type IconLayerName<Name extends LayeredIconName> =
  keyof (typeof iconLayerDefinitions)[Name]["defaults"] & string;

export type IconColors<Name extends LayeredIconName> = Partial<
  Record<IconLayerName<Name>, string>
>;

export const isLayeredIconName = (name: string): name is LayeredIconName =>
  name in iconLayerDefinitions;

export const getIconLayerColors = <Name extends LayeredIconName>(
  name: Name,
  overrides?: IconColors<Name>,
) => {
  const definition = iconLayerDefinitions[name];
  const defaults = definition.defaults as Record<string, string>;
  const resolvedOverrides = overrides as
    | Partial<Record<string, string>>
    | undefined;

  return definition.order.map(
    (layerName) => resolvedOverrides?.[layerName] ?? defaults[layerName],
  );
};
